import { Box, FormField, Heading, Text, TextButton } from '@wix/design-system'
import { Search } from '@wix/wix-ui-icons-common/lazy'
import classNames from 'classnames'
import React, { FC, useMemo } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import { AutoCompleteWithOverrides } from '../../reusable/Category/AutoCompleteWithOverrides'
import { useCategory } from '../../reusable/Category/useCategory'
import { FancyAutoComplete } from '../../reusable/FancyAutoComplete'
import ArrowLeft from '../../reusable/icons/ArrowLeft.svg'
import ArrowUpRight from '../../reusable/icons/ArrowUpRight.svg'
import Close from '../../reusable/icons/Close.svg'
import WixStudioLogo from '../../reusable/icons/WixStudioLogo.svg'
import s from './index.scss'

export const WixStudioCategory: FC = () => {
  const businessTermService = useMemo(() => new BusinessTermService(ambassadorHttpClient), [])

  const {
    handlers: {
      handleClear,
      handleInputClick,
      handleSearchSelect,
      handleNextClick,
      handleSearchChange,
      handleSkipClick,
      handleBackClick,
      handleSuggestionClick,
    },
    state: { search = '', category, categoriesLoading },
    categoriesOptions,
    content: {
      isAddUserInputAsOption,
      placeholder,
      title,
      subtitle,
      suggestionsLabel,
      suggestions,
      nextButton,
      skipButton,
      backButton,
    },
    autoCompleteRef,
    selectedId,
  } = useCategory({
    businessTermService,
  })

  const prefixNode = useMemo(
    () => (
      <Box height="100%" verticalAlign="middle">
        <Search className={s.searchIcon} />
      </Box>
    ),
    [],
  )

  const suffixNode = useMemo(
    () =>
      search ? (
        <Box height="100%" verticalAlign="middle">
          <TextButton onClick={categoriesLoading ? undefined : handleClear}>
            <Close className={s.closeIcon} />
          </TextButton>
        </Box>
      ) : null,
    [categoriesLoading, handleClear, search],
  )

  return (
    <div className={s.layout}>
      <div className={s.header}>
        <WixStudioLogo className={s.logo} />
        <hr className={s.divider} />
        <TextButton
          className={s.backButton}
          size="small"
          prefixIcon={<ArrowLeft className={s.backIcon} />}
          skin="dark"
          onClick={handleBackClick}
        >
          {backButton}
        </TextButton>
      </div>
      <div className={s.content}>
        <div className={s.headerContainer}>
          <Heading as="h1" className={s.title}>
            {title}
          </Heading>
          {subtitle && <Text className={s.subtitle}>{subtitle}</Text>}
        </div>
        <div className={s.modal}>
          <div className={s.formField}>
            <FormField>
              <FancyAutoComplete
                prefixNode={prefixNode}
                suffix={suffixNode}
                popoverProps={{ moveBy: { y: 10 } }}
                ref={autoCompleteRef}
                value={search}
                selectedId={selectedId}
                onChange={handleSearchChange}
                status={categoriesLoading ? 'loading' : undefined}
                options={categoriesOptions}
                placeholder={placeholder}
                size="large"
                onSelect={handleSearchSelect}
                onInputClicked={handleInputClick}
                className={classNames(s.searchField, { [s.notEmptySearchField]: search?.length })}
                clearButton={false}
                border="bottomLine"
                customRenderer={AutoCompleteWithOverrides}
                isIconHidden={isAddUserInputAsOption}
              />
            </FormField>
            <button className={s.nextButton} onClick={handleNextClick} disabled={!category}>
              {nextButton}
            </button>
          </div>
          <div className={s.listContainer}>
            {suggestionsLabel}
            <ul className={s.list}>
              {suggestions.items.map(({ text }, i) => (
                <li key={i}>
                  <button className={s.listItem} onClick={() => handleSuggestionClick(text, i)}>
                    {text}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <TextButton
          className={s.navigationButton}
          size="small"
          suffixIcon={<ArrowUpRight className={s.footerRightIcon} />}
          skin="dark"
          onClick={handleSkipClick}
        >
          {skipButton}
        </TextButton>
      </div>
    </div>
  )
}
